import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { signInWithPassword, getUserRoles, signOut, resetPasswordForEmail } from "@/integrations/firebase/auth";
import { db } from "@/lib/firebase";
import { doc, getDoc, collection, query, where, getDocs, updateDoc } from "firebase/firestore";
import { sanitizeEmail } from "@/lib/sanitize";
import { loginRateLimiter } from "@/lib/security";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { z } from "zod";

// Login validation schema
const loginSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
  password: z.string().min(1, { message: "Password is required" }),
});

const Auth = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  // Form fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Get the return URL from location state (set by protected routes)
  const returnUrl = (location.state as { from?: string })?.from || '/compliance';

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const sanitizedEmail = sanitizeEmail(email);

      if (!sanitizedEmail) {
        toast({
          title: "Invalid Email",
          description: "Please enter a valid email address",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Validate login input
      const validation = loginSchema.safeParse({ email: sanitizedEmail, password });
      if (!validation.success) {
        toast({
          title: "Validation Error",
          description: validation.error.errors[0].message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Check rate limiting
      if (!loginRateLimiter.isAllowed(sanitizedEmail)) {
        toast({
          title: "Too Many Login Attempts",
          description: "Please wait 5 minutes before trying again. This protects your account from unauthorized access.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const { data: authData, error } = await signInWithPassword({
        email: sanitizedEmail,
        password,
      });
      if (error) throw error;

      if (authData.user) {
        const roles = await getUserRoles(authData.user.uid);

        // Check email verification for service providers
        if (!roles.includes("client")) {
          const userDoc = await getDoc(doc(db, "users", authData.user.uid));

          if (userDoc.exists() && userDoc.data().emailVerified === false) {
            try {
              const verificationsRef = collection(db, "email_verifications");
              const verificationQuery = query(
                verificationsRef,
                where("userId", "==", authData.user.uid),
                where("verified", "==", true)
              );
              const verificationSnapshot = await getDocs(verificationQuery);

              if (!verificationSnapshot.empty) {
                await updateDoc(doc(db, "users", authData.user.uid), {
                  emailVerified: true,
                });
                console.log("Updated user emailVerified from verification record");
              } else {
                toast({
                  title: "Email Not Verified",
                  description: "Please check your email and verify your account before signing in.",
                  variant: "destructive",
                });
                await signOut();
                setLoading(false);
                return;
              }
            } catch (verificationError: any) {
              console.error("Error checking verification record:", verificationError);

              const refreshedUserDoc = await getDoc(doc(db, "users", authData.user.uid));
              if (refreshedUserDoc.exists() && refreshedUserDoc.data().emailVerified === true) {
                console.log("User emailVerified is true on refresh, proceeding...");
              } else {
                toast({
                  title: "Email Not Verified",
                  description: "Please check your email and verify your account before signing in.",
                  variant: "destructive",
                });
                await signOut();
                setLoading(false);
                return;
              }
            }
          }
        }
      }

      toast({
        title: "Welcome back!",
        description: "Signing you in...",
      });

      if (authData.user) {
        // Navigate to return URL (preserves /admin if that's where user came from)
        navigate(returnUrl, { replace: true });
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      toast({
        title: "Authentication Failed",
        description: error.message || "Invalid email or password",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);

    try {
      const sanitizedEmail = sanitizeEmail(resetEmail);

      if (!sanitizedEmail) {
        toast({
          title: "Invalid Email",
          description: "Please enter a valid email address",
          variant: "destructive",
        });
        setResetLoading(false);
        return;
      }

      const { error } = await resetPasswordForEmail(sanitizedEmail);

      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Reset Email Sent",
          description: "Check your inbox for password reset instructions.",
        });
        setShowForgotPassword(false);
        setResetEmail("");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send reset email",
        variant: "destructive",
      });
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[hsl(220,27%,8%)]"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        minHeight: '100vh',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div className="flex flex-col justify-center p-4 py-8" style={{ minHeight: 'calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))' }}>
        <Card className="w-full max-w-md mx-auto mb-8 bg-[hsl(220,22%,12%)] border-[hsl(220,15%,20%)]">
          <CardHeader className="text-center space-y-2">
            <div className="flex justify-center mb-4">
              <img src="/icons/normiz-favicon.svg" alt="Normiz" className="h-20 w-20" />
            </div>
            <CardTitle className="text-2xl text-white">NORMIZ</CardTitle>
            <CardDescription className="text-gray-400">
              Sign in to your account
            </CardDescription>
          </CardHeader>

          <CardContent className="pb-8 text-gray-200">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-300">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(true);
                      setResetEmail(email);
                    }}
                    className="text-sm text-[hsl(220,70%,60%)] hover:text-[hsl(220,70%,70%)] hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full bg-[hsl(220,70%,50%)] hover:bg-[hsl(220,70%,45%)]" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Sign In
              </Button>
            </form>

            {/* Forgot Password Form */}
            {showForgotPassword && (
              <div className="mt-6 pt-6 border-t border-[hsl(220,15%,25%)]">
                <h3 className="text-sm font-medium text-white mb-3">Reset your password</h3>
                <form onSubmit={handleForgotPassword} className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email" className="text-gray-300 text-sm">Email address</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="you@company.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                      autoComplete="email"
                      className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setResetEmail("");
                      }}
                      className="flex-1 border-[hsl(220,15%,25%)] text-gray-300 hover:bg-[hsl(220,15%,20%)] hover:text-white"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={resetLoading}
                      className="flex-1 bg-[hsl(220,70%,50%)] hover:bg-[hsl(220,70%,45%)]"
                    >
                      {resetLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Send Reset Link
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
